// Admin user manual content (Mirabili).
//
// Hardcoded TS data so it's type-checked + ships with the deploy
// (no markdown renderer dep). One source of truth for the Help
// page.
//
// Each block is ACTION-ORIENTED: a `heading`, an ordered `steps`
// list ("go to X → do Y → see Z"), and an optional `note` (a gotcha
// or "why"). Nav paths match AdminShell labels.

export interface HelpBlock {
  heading: string;
  steps: string[];
  note?: string;
}

export interface HelpSection {
  id: string;
  title: string;
  icon: string;
  summary: string;
  blocks: HelpBlock[];
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    icon: '🗺️',
    summary: 'Mirabili turns engineering work into a game. XP drives Forbury competency progression; coins are the fun, spendable side.',
    blocks: [
      {
        heading: 'Set up a new installation',
        steps: [
          'Open Admin → Teams, create your first team (name + slug).',
          'Open Admin → Ingestors → New, pick "Jira", choose the team, and fill the config.',
          'In the ingestor\u2019s Secret, paste the Jira API token (id.atlassian.com → API tokens → Create). Click Save, then Test connection — it must return ok.',
          'Open Admin → Scoring rules and confirm at least one rule exists (seed rules usually do).',
          'Open Admin → Chronicles → enable + set a theme (or keep the default).',
          'Open Admin → Public board to pick which panels the wall TV rotates.',
        ],
        note: 'The Setup status panel at the top of this page tells you what\u2019s still missing at a glance.',
      },
    ],
  },
  {
    id: 'adventures',
    title: 'Adventures',
    icon: '⚔️',
    summary: 'A sprint as a game: commit to tickets, snapshot on Start, get scored on End.',
    blocks: [
      {
        heading: 'Create an adventure from a Jira sprint',
        steps: [
          'Open Admin → Adventures → New adventure.',
          'Pick the team.',
          'In "Import from Jira", pick a sprint (or type a name + dates manually).',
          'Click Submit — you land on the adventure\u2019s detail page as a draft.',
        ],
        note: 'If no sprints appear: the Jira token is missing/wrong, or the team\u2019s sprintField (Ingestor config) is wrong for your project.',
      },
      {
        heading: 'Add goals',
        steps: [
          'On the detail page, Goals tab.',
          'Shape A (ticket bundle): name it, tick the tickets from the sprint\u2019s commitment, set "hit when all/any closed".',
          'Shape B (count metric): name it, set the event type (e.g. pr.closed) and a target count.',
          'Add as many as you like.',
        ],
        note: 'Edit a goal\u2019s tickets while it\u2019s a draft only. After Start, goals are locked.',
      },
      {
        heading: 'Run the sprint',
        steps: [
          'When ready, click "Start adventure" (detail page). It snapshots the commitment + closes any active adventure for the team.',
          'Watch the live predictability panel tick up as tickets close (it polls every 15s).',
          'At sprint end, click "End adventure". The system scores it and distributes bonus coins.',
        ],
      },
      {
        heading: 'Tune the scoring knobs',
        steps: [
          'Open Admin → Adventures (the list).',
          'Pick a team in the filter — the "Scoring settings" panel appears.',
          'Adjust predictability threshold, per-tier coin amounts, reward pool size, or terminal status names. Save.',
        ],
      },
    ],
  },
  {
    id: 'bosses',
    title: 'Boss Fights',
    icon: '👹',
    summary: 'Major initiatives (Jira epics) the whole team fights together.',
    blocks: [
      {
        heading: 'Create + latch a boss',
        steps: [
          'Open Admin → Boss Fights → New boss.',
          'Pick the team, then "Import from Jira epic" to pick an epic (fills name + HP pool).',
          'Set the HP multiplier (default 1).',
          'Pick a reward badge (awarded to the whole team on a win).',
          'Click Submit, then "Latch boss" on the detail page — it snapshots the epic\u2019s children as HP.',
        ],
        note: 'HP = sum of story points on the epic\u2019s tickets × multiplier.',
      },
      {
        heading: 'Fight + close',
        steps: [
          'Watch the red HP bar (admin detail page + wall-TV ribbon) as the team deals damage.',
          'At the window end, click "End fight". The system computes final damage.',
          'If damage ≥ HP: vanquished, and the reward badge is granted to every team member.',
        ],
        note: 'Damage = XP from the boss\u2019s qualifying event types (done-transitions, merged PRs, reviews, comments). Tune them per boss so reviewers + support can contribute.',
      },
    ],
  },
  {
    id: 'chronicles',
    title: 'Chronicles',
    icon: '📖',
    summary: 'The book page that narrates the day as fantasy prose.',
    blocks: [
      {
        heading: 'Configure the theme',
        steps: [
          'Open Admin → Chronicles.',
          'Edit the theme (name, setting, voice, word limit). Keep the JSON valid.',
          'Click Regenerate to re-render existing chapters with the new theme.',
        ],
      },
      {
        heading: 'Read it',
        steps: [
          'Open /public-board/chronicles (or the Chronicles nav item).',
          'It flows newest-at-bottom. Click "Show all" to see the full day.',
          'Adventure recaps appear here as normal paragraphs when an adventure closes.',
        ],
      },
    ],
  },
  {
    id: 'ingestors',
    title: 'Ingestors',
    icon: '🔌',
    summary: 'The pipes that bring events in from Jira, GitHub, etc.',
    blocks: [
      {
        heading: 'Set up the Jira ingestor',
        steps: [
          'Open Admin → Ingestors → New, choose type "Jira", pick the team.',
          'In Config: set baseUrl (https://yourorg.atlassian.net), email, authMode=basic, projectKey, storyPointsField (default customfield_10024), sprintField (default customfield_10020).',
          'In Secret: paste the API token. Save.',
          'Click Test connection — must return ok.',
        ],
        note: 'If your project uses different custom field ids for story points or sprints, override them here. Getting these wrong is the #1 cause of "no sprints / no points" confusion.',
      },
      {
        heading: 'Fix "no sprints show up"',
        steps: [
          'Confirm the API token is present (open the ingestor → Secret). Re-enter it if needed.',
          'Confirm the team has sprintField set in the ingestor config.',
          'Use Test connection to verify the token works before debugging anything else.',
        ],
      },
    ],
  },
  {
    id: 'scoring',
    title: 'Scoring & Progression',
    icon: '📈',
    summary: 'How events become XP, coins, competencies, tiers, badges, loot.',
    blocks: [
      {
        heading: 'Add a scoring rule',
        steps: [
          'Open Admin → Scoring rules → New rule.',
          'Pick the team, event type, and priority (smaller = runs earlier).',
          'Set XP + competency split(s). Optionally add conditions.',
          'Save — the rule applies to new events immediately.',
        ],
      },
      {
        heading: 'Configure the status-transition matrix',
        steps: [
          'Open Admin → Status transitions (via the ingestor\u2019s detail page or Scoring rules).',
          'Map each Jira transition (e.g. "To Do → In Progress") to XP.',
          'Done / Cancelled are what adventures count as shipped.',
        ],
      },
      {
        heading: 'Award a badge or grant XP/coins',
        steps: [
          'Open Admin → Users, find the player.',
          'Use Grant XP/coins (adds with an audit reason).',
          'Badges auto-award when their criteria fire — no manual step.',
        ],
      },
    ],
  },
  {
    id: 'walltv',
    title: 'Wall TV & Public Board',
    icon: '🖥️',
    summary: 'The full-screen monitor view teams watch.',
    blocks: [
      {
        heading: 'Set up the wall TV',
        steps: [
          'Open Admin → Public board.',
          'Toggle which panels to rotate (Live feed, Just shipped, On fire, etc.).',
          'Set rotation seconds, anonymise, sound, live-feed timeout.',
          'Open /public-board on the wall monitor. It auto-switches to the three-panel layout while a sprint is active.',
        ],
      },
    ],
  },
  {
    id: 'mentor',
    title: 'Mentor Mode',
    icon: '✨',
    summary: 'Deliberate, opt-in teaching. Turn it on, do work, close it. Not a passive XP buff.',
    blocks: [
      {
        heading: 'Use mentor mode (players)',
        steps: [
          'On your player dashboard, click "Start mentor mode".',
          'Optionally add a note ("pairing on the auth rewrite").',
          'Do the teaching work — reviews, pairing, a design walk-through.',
          'Click "End session" when done.',
          '3 qualifying sessions (real note, 30+ min, real events) unlock the ✨ Mentor badge.',
        ],
        note: 'Work during an active session earns a +50% XP bonus, capped at +5 per event. The cap keeps it honest — it can’t be farmed into a general XP multiplier.',
      },
      {
        heading: 'Spot-check sessions (admins)',
        steps: [
          'Open Admin → Audit log and filter action = mentor_session.end.',
          'Each row shows the note + whether it qualified, for retrospective review.',
        ],
      },
    ],
  },
];
